const {
  login: catalystLogin,
  loginWithToken: catalystLoginWithToken,
  logout: catalystLogout,
  softLogin: catalystSoftLogin,
  isLoggedIn: catalystIsLoggedIn,
  userStatus: catalystUserStatus,
  resetPasswordWithCurrent: catalystResetPasswordWithCurrent,
  resetPasswordWithoutCurrent: catalystResetPasswordWithoutCurrent,
  legalTextConsent: catalystLegalTextConsent,
  consentTemplate: catalystConsentTemplate,
  consentTemplateV2: catalystConsentTemplateV2,
  saveUserConsent: catalystSaveUserConsent,
  getOTPByEmailURL: catalystOtpByEmail,
  getOTPBySMSURL: catalystOtpBySMS,
  getOTPByChallengeURL: catalystOtpByChallenge,
  getOTPChallengeValidationURL: catalystOtpChallengeValidation,
  getOTPValidated: catalystOtpValidation,
  updateUser: catalystUpdateUser,
  getUser: catalystGetUser,
} = require('./catalystActions');

const {
  login: makeAgentLogin,
  logout: makeAgentLogout,
  userImpersonationLogin,
} = require('./agentActions');

const { withErrorTranslation } = require('./lib/errorTranslation');
const { decodeJWT } = require('./utils/decoder');
const {
  getConsentDateInISO8601Format,
  getConsentDateInLocalTimeFormatWithTimeZone,
} = require('./utils/dateTimeUtils');
const { getConsentBody, generateDeviceIdentifier } = require('./utils/consent');
const { setCookie, setItemInSessionStorage } = require('./lib/browserData');

const login = async ({
  email,
  password,
  tenantId,
  doMergeCart = false,
  doAssociateAccount = false,
  catalystBaseUrl = '',
  additionalInfo = {},
  isEnableNewOtpFlow,
  isEmailPrefixEligibleForOtpModal,
}) =>
  await catalystLogin(
    email.trim(),
    password,
    tenantId,
    doMergeCart,
    doAssociateAccount,
    catalystBaseUrl,
    false,
    undefined,
    undefined,
    additionalInfo,
    isEnableNewOtpFlow,
    isEmailPrefixEligibleForOtpModal,
  );

const loginWithToken = async ({ loginDataResponse, tenantId, additionalInfo = {} }) =>
  await catalystLoginWithToken(loginDataResponse, tenantId, additionalInfo);

const agentLogin = async ({
  tenantId,
  adfsAccessToken,
  adfsRefreshToken,
  adfsIdToken,
  agentBaseUrl = '',
  customInfo,
  isKiosko,
  additionalInfo,
}) =>
  await makeAgentLogin(
    adfsAccessToken,
    adfsRefreshToken,
    adfsIdToken,
    customInfo,
    tenantId,
    agentBaseUrl,
    isKiosko,
    additionalInfo,
  );

const impersonationLogin = async ({
  emailAddress,
  tenantId,
  agentBaseUrl = '',
  flow,
  additionalInfo,
}) => userImpersonationLogin(emailAddress, tenantId, agentBaseUrl, flow, additionalInfo);

const loginWithOTP = async ({
  email,
  otp,
  tenantId,
  doMergeCart = false,
  doAssociateAccount = false,
  catalystBaseUrl = '',
  otpType = null,
  additionalHeaders = {},
  additionalInfo = {},
}) =>
  catalystLogin(
    email.trim(),
    otp,
    tenantId,
    doMergeCart,
    doAssociateAccount,
    catalystBaseUrl,
    true,
    otpType,
    additionalHeaders,
    additionalInfo,
  );

const softLogin = async ({ email, tenantId, catalystBaseUrl = '', additionalInfo = {} }) =>
  await catalystSoftLogin(email.trim(), tenantId, catalystBaseUrl, additionalInfo);

const logout = async ({ tenantId, catalystBaseUrl = '', additionalInfo = {}, otpFlow = false }) =>
  await catalystLogout(tenantId, catalystBaseUrl, additionalInfo, otpFlow);

const agentLogout = async ({ tenantId, agentBaseUrl = '', additionalInfo }) =>
  await makeAgentLogout(tenantId, agentBaseUrl, additionalInfo);

const isLoggedIn = async ({ baseUrl = '', cookies, channelId = 'WEB', tenantId, additionalInfo }) =>
  await catalystIsLoggedIn(baseUrl, cookies, channelId, tenantId, additionalInfo);

const userStatus = async ({ tenantId, catalystBaseUrl = '', params }) =>
  await catalystUserStatus(tenantId, catalystBaseUrl, params);

const resetPasswordWithCurrent = async ({
  currentPassword,
  newPassword,
  tenantId,
  catalystBaseUrl = '',
}) =>
  await catalystResetPasswordWithCurrent(currentPassword, newPassword, tenantId, catalystBaseUrl);

const resetPasswordWithoutCurrent = async ({
  newPassword,
  confirmNewPassword,
  tenantId,
  catalystBaseUrl = '',
}) =>
  await catalystResetPasswordWithoutCurrent(
    newPassword,
    confirmNewPassword,
    tenantId,
    catalystBaseUrl,
  );

const legalTextConsent = async ({ consentId, tenantId, catalystBaseUrl = '' }) =>
  await catalystLegalTextConsent(consentId, tenantId, catalystBaseUrl);

const consentTemplate = async ({ purpose, tenantId, catalystBaseUrl = '' }) =>
  await catalystConsentTemplate(purpose, tenantId, catalystBaseUrl);

const consentTemplateV2 = async ({ userEmail, purpose, tenantId, catalystBaseUrl = '' }) =>
  await catalystConsentTemplateV2(userEmail, purpose, tenantId, catalystBaseUrl);

const saveUserConsent = async ({ consent, tenantId, catalystBaseUrl = '' }) =>
  await catalystSaveUserConsent(consent, tenantId, catalystBaseUrl);

const getOTPByEmailURL = async ({ emailAddress, tenantId, catalystBaseUrl, otpFlow }) =>
  await catalystOtpByEmail(emailAddress, tenantId, catalystBaseUrl, otpFlow);

const getOTPBySMSURL = async ({ emailAddress, tenantId, catalystBaseUrl, otpFlow }) =>
  await catalystOtpBySMS(emailAddress, tenantId, catalystBaseUrl, otpFlow);

const getOTPByChallengeURL = async ({ tenantId, catalystBaseUrl, isEmailFlow, otpFlow }) =>
  await catalystOtpByChallenge(tenantId, catalystBaseUrl, isEmailFlow, otpFlow);

const getOTPChallengeValidationURL = async ({
  tenantId,
  challengeId,
  otp,
  catalystBaseUrl,
  doAssociateAccount,
  doMergeCart,
  isEmailFlow,
  otpFlow,
  additionalInfo,
}) =>
  await catalystOtpChallengeValidation(
    tenantId,
    challengeId,
    otp,
    catalystBaseUrl,
    doAssociateAccount,
    doMergeCart,
    isEmailFlow,
    otpFlow,
    additionalInfo,
  );

const getOTPValidated = async ({
  emailAddress,
  otp,
  otpType,
  tenantId,
  catalystBaseUrl,
  otpFlow,
}) => await catalystOtpValidation(emailAddress, otp, otpType, tenantId, catalystBaseUrl, otpFlow);

const updateUser = async ({ userData, tenantId, catalystBaseUrl = '' }) =>
  await catalystUpdateUser(userData, tenantId, catalystBaseUrl);

const getUser = async ({ tenantId, catalystBaseUrl = '' }) =>
  await catalystGetUser(tenantId, catalystBaseUrl);

module.exports = {
  decodeJWT,
  login: withErrorTranslation(login, 'LOGIN'),
  loginWithToken: withErrorTranslation(loginWithToken, 'LOGIN_WITH_TOKEN'),
  loginWithOTP: withErrorTranslation(loginWithOTP, 'OTP_LOGIN'),
  agentLogin: withErrorTranslation(agentLogin, 'LOGIN'),
  softLogin: withErrorTranslation(softLogin, 'SOFT_LOGIN'),
  logout: withErrorTranslation(logout, 'LOGOUT'),
  agentLogout: withErrorTranslation(agentLogout, 'LOGOUT'),
  isLoggedIn,
  impersonationLogin,
  userStatus: withErrorTranslation(userStatus, 'USER_STATUS'),
  resetPasswordWithCurrent: withErrorTranslation(resetPasswordWithCurrent, 'RESET_PASSWORD'),
  resetPasswordWithoutCurrent: withErrorTranslation(resetPasswordWithoutCurrent, 'RESET_PASSWORD'),
  legalTextConsent: withErrorTranslation(legalTextConsent, 'LEGAL_TEXT'),
  consentTemplate: withErrorTranslation(consentTemplate, 'CONSENT_TEMPLATE'),
  consentTemplateV2: withErrorTranslation(consentTemplateV2, 'CONSENT_TEMPLATE'),
  saveUserConsent: withErrorTranslation(saveUserConsent, 'SAVE_CONSENT'),
  getOTPByEmailURL: withErrorTranslation(getOTPByEmailURL, 'OTP_BY_EMAIL'),
  getOTPBySMSURL: withErrorTranslation(getOTPBySMSURL, 'OTP_BY_EMAIL'),
  getOTPByChallengeURL: withErrorTranslation(getOTPByChallengeURL, 'OTP_BY_CHALLENGE'),
  getOTPChallengeValidationURL: withErrorTranslation(
    getOTPChallengeValidationURL,
    'OTP_BY_CHALLENGE_VALIDATIONS',
  ),
  getOTPValidated: withErrorTranslation(getOTPValidated, 'OTP_VALIDATION'),
  updateUser: withErrorTranslation(updateUser, 'UPDATE_USER'),
  getUser: withErrorTranslation(getUser, 'GET_USER'),
  getConsentDateInISO8601Format,
  getConsentBody,
  generateDeviceIdentifier,
  getConsentDateInLocalTimeFormatWithTimeZone,
  setCookie,
  setItemInSessionStorage,
};
